/**
 * IconButton — square 44px icon-only button used in the topbar (notifications,
 * help, menu) and other dense chrome areas. Optional `ping` dot for unread
 * indicators (notifications).
 *
 * Why local to features/workspace and not in @flint/ui: the topbar is the
 * only consumer for now; if a second consumer needs the same shape, lift
 * to the primitives package then (anti-pattern: The Premature Primitive —
 * promote when used twice, not preemptively).
 */
import * as React from "react";
import { cn } from "@flint/ui";

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Show an unread-indicator ping at top-right. */
  ping?: boolean;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, children, ping, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "relative inline-flex items-center justify-center h-11 w-11 rounded-sm border border-line bg-paper text-ink-2 hover:bg-raised hover:border-line-2 transition-colors disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
      {ping ? (
        <span
          aria-hidden
          className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-bad ring-2 ring-paper"
        />
      ) : null}
    </button>
  );
});
